import type { FragmentOf } from "@opensea/graphql"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import { Flex } from "@opensea/ui-kit"
import { useEffect, useState } from "react"
import { useTranslations } from "use-intl"
import { Button } from "../../../../components/Button"
import type { WalletDestination } from "../../../../components/DestinationWalletSelect/types"
import { TransactionFullScreen } from "../../../../components/TransactionFullScreen"
import {
  TransactionReceipt,
  TransactionReceiptCollectionFragment,
} from "../../../../components/TransactionReceipt"
import { TransactionReceiptFragment } from "../../../../components/TransactionReceipt/TransactionReceipt.fragment"
import { ViewItemsOnProfileButton } from "../../../../components/ViewItemsOnProfileButton"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../../constants"
import { FlowActions } from "../../../../stores/flowStore"
import type { Item, ItemQuoteInput } from "../../../../types"
import { useSetReceiptStatusFragment } from "../../../hooks/useSetReceiptStatus"

const FETCH_INTERVAL = 500

const receiptQuery = graphql(
  `
    query MintFlowStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
      $swapProvider: SwapProviderType!
    ) {
      buyReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
        swapProvider: $swapProvider
        action: MINT
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
            imageUrl
          }
        }
        failedItemReceipts {
          __typename
        }
        ...useSetReceiptStatus
        ...TransactionReceipt
      }
    }
  `,
  [TransactionReceiptFragment, useSetReceiptStatusFragment],
)

export const MintStatusCollectionFragment = graphql(
  `
    fragment MintStatusCollection on Collection {
      imageUrl
      ...TransactionReceiptCollection
    }
  `,
  [TransactionReceiptCollectionFragment],
)

type Props = {
  collection: FragmentOf<typeof MintStatusCollectionFragment>
  transactionIdentifier: {
    chain: string
    transactionHash: string
  }
  tokenInputs: Item[]
  isCrossChain: boolean
  itemQuotes: ItemQuoteInput[]
  toAsset?: {
    contractAddress: string
    chain: string
    tokenId: string
  }
  walletDestination?: WalletDestination
}

export function MintStatus({
  collection: collectionKey,
  transactionIdentifier,
  tokenInputs,
  isCrossChain,
  itemQuotes,
  walletDestination,
}: Props) {
  const t = useTranslations("wallet.MintStatus")
  const collection = readFragment(MintStatusCollectionFragment, collectionKey)
  const [receiptOpen, setReceiptOpen] = useState(false)

  const [{ data, fetching }, reexecuteQuery] = useQuery({
    query: receiptQuery,
    variables: {
      transactionIdentifiers: [
        {
          chain: transactionIdentifier.chain,
          transactionHash: transactionIdentifier.transactionHash,
        },
      ],
      itemQuotes,
      swapProvider: isCrossChain ? "RELAY" : "LOCAL_MINT_NFT",
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  const isPending = !data || data.buyReceipt.status === "PENDING"
  // For mints it's possible that the items are not yet ingested when receipt is returned
  const allItemsLoaded = data?.buyReceipt.itemReceipts.every(itemReceipt =>
    Boolean(itemReceipt.item),
  )

  useEffect(
    function pollForReceipt() {
      if ((isPending || !allItemsLoaded) && !fetching) {
        const timerId = setTimeout(() => {
          reexecuteQuery({ requestPolicy: "network-only" })
        }, FETCH_INTERVAL)

        return () => clearTimeout(timerId)
      }
    },
    [allItemsLoaded, fetching, isPending, reexecuteQuery],
  )

  useEffect(
    function handleSuccess() {
      if (data?.buyReceipt.status === "SUCCESS") {
        FlowActions.onSuccess()
      }
    },
    [data?.buyReceipt.status],
  )

  if (isPending) {
    return (
      <TransactionFullScreen
        body={t("pending.description")}
        images={tokenInputs.flatMap(item =>
          new Array(item.quantity).fill(collection.imageUrl),
        )}
        loading
        title={t("pending.title")}
      />
    )
  }

  if (data.buyReceipt.status === "FAILED") {
    return (
      <TransactionFullScreen
        body={t("failed.description")}
        images={tokenInputs.flatMap(item =>
          new Array(item.quantity).fill(collection.imageUrl),
        )}
        monochrome
        title={t("failed.title")}
      />
    )
  }

  const images = data.buyReceipt.itemReceipts.map(
    itemReceipt => itemReceipt.item?.imageUrl ?? collection.imageUrl,
  )

  return (
    <TransactionFullScreen
      bottom={
        receiptOpen ? (
          <TransactionReceipt
            collection={collection}
            flowType="mint"
            receipt={data.buyReceipt}
            walletDestination={walletDestination}
          />
        ) : (
          <Flex className="gap-4">
            <Button onClick={() => setReceiptOpen(true)} variant="secondary">
              {t("viewReceipt")}
            </Button>
            <ViewItemsOnProfileButton
              itemIds={data.buyReceipt.itemReceipts.flatMap(itemReceipt =>
                itemReceipt.item ? [itemReceipt.item.id] : [],
              )}
              walletDestinationAddress={walletDestination?.address}
            />
          </Flex>
        )
      }
      confetti
      images={images}
      title={t("success.title")}
    />
  )
}
